import { useNotificationDeliveries, useRedeliverNotification } from '../../hooks/useNotifications';
import { Button } from '../common/Button';
import { Skeleton } from '../common/Skeleton';
import { useToast } from '../common/Toast';
import { apiError } from '../../utils/apiError';
import { formatDateTime } from '../../utils/datetime';
import { NotificationChannel, NotificationDelivery } from '../../types';

function StatusChip({ d }: { d: NotificationDelivery }) {
  const base = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium';
  if (d.status === 'success') {
    return <span className={`${base} bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300`}>Delivered</span>;
  }
  if (d.status === 'failed' && d.next_retry_at) {
    return <span className={`${base} bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300`}>Retrying</span>;
  }
  if (d.status === 'failed') {
    return <span className={`${base} bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300`}>Failed</span>;
  }
  return <span className={`${base} bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300`}>Pending</span>;
}

interface Props {
  channel: NotificationChannel;
  canRedeliver: boolean;
}

/**
 * Recent deliveries for one channel, newest first, with a manual redeliver.
 * Retries are scheduled in the database and drained once a minute, so a
 * "Retrying" row will be attempted again even across a restart; "Failed"
 * means all three attempts are spent and only a redeliver will send it.
 */
export function NotificationDeliveries({ channel, canRedeliver }: Props) {
  const { data, isLoading } = useNotificationDeliveries(channel.id);
  const redeliver = useRedeliverNotification();
  const { addToast } = useToast();

  if (isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">No deliveries yet. Notifications for subscribed events will appear here.</p>;
  }

  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full min-w-[40rem] text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <th scope="col" className="px-2 py-2">When</th>
            <th scope="col" className="px-2 py-2">Event</th>
            <th scope="col" className="px-2 py-2">Status</th>
            <th scope="col" className="px-2 py-2">Attempt</th>
            <th scope="col" className="px-2 py-2">Response</th>
            {canRedeliver && <th scope="col" className="px-2 py-2 text-right">Actions</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {data.map(d => (
            <tr key={d.id}>
              <td className="px-2 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{formatDateTime(d.created_at)}</td>
              <td className="px-2 py-2 capitalize text-gray-700 dark:text-gray-300">{d.event}</td>
              <td className="px-2 py-2"><StatusChip d={d} /></td>
              <td className="px-2 py-2 text-gray-600 dark:text-gray-400">
                {d.attempt}/3
                {d.next_retry_at && <span className="block text-xs text-gray-500">next {formatDateTime(d.next_retry_at)}</span>}
              </td>
              <td className="px-2 py-2 text-gray-600 dark:text-gray-400 max-w-[16rem] truncate" title={d.error_message ?? d.response_body ?? ''}>
                {d.response_code !== null && <span className="font-mono mr-1">{d.response_code}</span>}
                {d.error_message ?? (d.response_body ? d.response_body.slice(0, 80) : '')}
              </td>
              {canRedeliver && (
                <td className="px-2 py-2 text-right">
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={redeliver.isPending && redeliver.variables?.deliveryId === d.id}
                    onClick={() => redeliver.mutate({ id: channel.id, deliveryId: d.id }, {
                      onSuccess: r => addToast(
                        r.status === 'success' ? 'success' : 'error',
                        r.status === 'success' ? 'Redelivered' : `Redelivery failed: ${r.error_message ?? 'unknown error'}`,
                      ),
                      onError: err => addToast('error', apiError(err, 'Failed to redeliver')),
                    })}
                  >
                    Redeliver
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
